/**
 * Hoje no fuso do navegador, em `yyyy-MM-dd`, para `<input type="date">`.
 *
 * `new Date().toISOString().split('T')[0]` é o dia em UTC: depois das 21h em Brasília
 * ele já é amanhã — e um "Pago em" de amanhã é recusado pelo servidor.
 */
export function localTodayInput(): string {
    const now = new Date()
    const mm = String(now.getMonth() + 1).padStart(2, '0')
    const dd = String(now.getDate()).padStart(2, '0')
    return `${now.getFullYear()}-${mm}-${dd}`
}

/**
 * Converte um texto colado pelo usuário em um valor `yyyy-MM-dd`, aceito por
 * `<input type="date">`.
 *
 * O input nativo de data ignora a colagem de texto: o navegador só aceita a
 * digitação segmento a segmento. Colar "17/07/1990" não preenche nada. Este
 * parser cobre os formatos que o usuário tende a copiar de planilhas, prontuários
 * e documentos.
 *
 * Formatos aceitos (separadores `/`, `-`, `.` ou espaço):
 * - `dd/MM/yyyy` e `d/M/yyyy`
 * - `yyyy-MM-dd` (ISO; também aceita um sufixo de horário, que é descartado)
 * - `ddMMyyyy` (8 dígitos, sem separador)
 *
 * A data é validada de fato: "31/02/2020" e "00/01/2020" são rejeitados. Anos
 * com menos de 4 dígitos são rejeitados, porque "17/07/90" é ambíguo.
 *
 * @returns o valor em `yyyy-MM-dd`, ou `null` se o texto não for uma data válida.
 */
export function parsePastedDate(text: string): string | null {
    const input = text.trim()
    if (!input) return null

    let year: number
    let month: number
    let day: number

    const iso = input.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[T\s].*)?$/)
    const dmy = input.match(/^(\d{1,2})[-/.\s](\d{1,2})[-/.\s](\d{4})$/)
    const digits = input.match(/^(\d{2})(\d{2})(\d{4})$/)

    if (iso) {
        year = Number(iso[1])
        month = Number(iso[2])
        day = Number(iso[3])
    } else if (dmy) {
        day = Number(dmy[1])
        month = Number(dmy[2])
        year = Number(dmy[3])
    } else if (digits) {
        day = Number(digits[1])
        month = Number(digits[2])
        year = Number(digits[3])
    } else {
        return null
    }

    if (month < 1 || month > 12 || day < 1 || day > 31) return null

    // Rejeita datas que "transbordam" o mês (ex.: 31/02). O Date normaliza
    // silenciosamente, então comparamos os componentes de volta. UTC evita que o
    // fuso local desloque o dia.
    const parsed = new Date(Date.UTC(year, month - 1, day))
    if (
        parsed.getUTCFullYear() !== year ||
        parsed.getUTCMonth() !== month - 1 ||
        parsed.getUTCDate() !== day
    ) {
        return null
    }

    const mm = String(month).padStart(2, '0')
    const dd = String(day).padStart(2, '0')
    return `${year}-${mm}-${dd}`
}

/**
 * Handler de `onPaste` para `<input type="date">`.
 *
 * Quando o texto colado é uma data reconhecível, cancela a colagem nativa (que
 * seria descartada pelo navegador) e entrega o valor já em `yyyy-MM-dd`. Se o
 * texto não for uma data válida, deixa o comportamento padrão seguir.
 */
export function handleDateInputPaste(
    event: React.ClipboardEvent<HTMLInputElement>,
    onParsed: (isoDate: string) => void,
): void {
    const pasted = event.clipboardData.getData('text')
    const isoDate = parsePastedDate(pasted)
    if (!isoDate) return

    event.preventDefault()
    onParsed(isoDate)
}
